import { useEffect, useState } from "react";
import { Button } from "@astryxdesign/core/Button";
import { Text } from "@astryxdesign/core/Text";
import { Smartphone } from "lucide-react";
import { InfoBlock } from "./InfoBlock";
import { isIos, isStandalone } from "@/lib/pwa";
import { useT } from "@/lib/i18n";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** Install guidance that matches the platform: native prompt where it exists, steps on iOS. */
export function InstallHint({ name }: { name: string }) {
  const t = useT();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;
  return (
    <InfoBlock icon={Smartphone} title={t.install.title}>
      {deferred ? (
        <Button
          label={t.install.install(name)}
          variant="secondary"
          width="100%"
          onClick={async () => {
            await deferred.prompt();
            const choice = await deferred.userChoice;
            if (choice.outcome === "accepted") setInstalled(true);
            setDeferred(null);
          }}
        />
      ) : isIos() ? (
        <Text type="body">{t.install.ios}</Text>
      ) : (
        <Text type="body">{t.install.other}</Text>
      )}
    </InfoBlock>
  );
}
